import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useRelayEnvironment } from 'react-relay';

import FlashesStore from 'src/stores/FlashesStore';
import logger from 'src/util/logger';

import { getPropertyDataInBatches, getTimeSeriesData } from '../helpers/dataBatching';
import { PropertyShowLoadingActions } from '../reducer/constants';

/**
 * Custom hook that encapsulates all property data fetching.
 *
 * Handles:
 * - Batched data loading with progress tracking via reducer dispatch
 * - Data transformation from GraphQL response to meter objects
 * - Error handling and user feedback
 *
 * Loading state is managed externally via the stateDispatchFunc reducer.
 * @param {object} params - Hook parameters
 * @param {string} params.propertyId - The property UUID to fetch data for
 * @param {object} params.dateSpan - Object with start and finish Luxon DateTime objects
 * @param {string} params.timezone - Timezone of the property
 * @param {object} params.initialData - Static/initial property data
 * @param {string} params.historianAggregation - Aggregation level
 * for historian data (e.g., 'PT30M', 'P1D')
 * @param {string} params.tradeAggregation - Aggregation level for trade data
 * @param {Function} [params.stateDispatchFunc] - Dispatch function for loading state reducer
 * @returns {object} Object containing:
 *   - combinedData: {object} Combined property data from GraphQL responses
 */
const usePropertyData = ({
  propertyId,
  dateSpan,
  timezone,
  initialData,
  historianAggregation,
  tradeAggregation,
  stateDispatchFunc,
}) => {
  const environment = useRelayEnvironment();

  const [combinedData, setCombinedData] = useState(null);

  // Cache static data (meters, rules, property info) to avoid refetching on dateSpan change
  const staticDataCache = useRef({
    meters: null,
    rulesData: null,
    hasStaticData: false,
  });

  // Track if this is the initial load (first effect is handling it)
  const isInitialLoadRef = useRef(true);

  // Track if we're currently fetching data to prevent parallel fetches
  const isFetchingData = useRef(false);

  // Per-request cancellation token. Each new request creates a fresh { current: false }
  // object and stores it here. cancelFunc sets the active token's .current to true.
  // Old in-flight requests keep their own token reference, so they stay cancelled even
  // when a newer request starts — preventing the race where resetting a shared boolean
  // would "uncancel" a stale request.
  const cancelTokenRef = useRef({ current: false });

  // When true, the second effect skips its next fetch (used when reverting
  // datespan after cancellation — the data is already in state).
  const skipNextFetchRef = useRef(false);

  // First effect: Fetch static data (meters, rules) once per propertyId
  useEffect(() => {
    if (!propertyId) {
      logger.error('No property ID found for property data fetching');
      return;
    }

    // Only fetch if we don't have cached static data
    if (staticDataCache.current.hasStaticData) {
      logger.log('Using cached static data (meters, rules)');
      return;
    }

    logger.log('Fetching static property data (meters, rules) for property:', propertyId);

    if (stateDispatchFunc) {
      stateDispatchFunc({ type: PropertyShowLoadingActions.START_REQUEST });
    }

    // Create a fresh cancellation token for this request. Old in-flight requests
    // retain their own (cancelled) token, so they cannot be "uncancelled".
    const cancelToken = { current: false };
    cancelTokenRef.current = cancelToken;

    getPropertyDataInBatches({
      propertyId,
      dateSpan,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      initialData,
      isFetchingRef: isFetchingData,
      cancelledRef: cancelToken,
      onDataReceived: (mergedData) => {
        logger.log('Received merged property data with property:', !!mergedData?.property);
        // Cache the static data for future dateSpan changes
        if (mergedData?.property?.meters?.edges) {
          const meters = mergedData.property.meters.edges.map((edge) => edge.node);
          const rulesData = meters.map((meter) => ({
            id: meter.id,
            rules: meter.rules,
          }));
          staticDataCache.current = {
            meters,
            rulesData,
            hasStaticData: true,
          };
          logger.log('Cached static data:', meters.length, 'meters');
        }
        // Mark initial load as complete so second effect doesn't run
        isInitialLoadRef.current = false;
        setCombinedData(mergedData);
        // Dispatch fully loaded action to reducer
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PropertyShowLoadingActions.MARK_FULLY_LOADED });
        }
      },
      onError: (fetchError, message) => {
        logger.error('Error fetching property data:', fetchError);
        FlashesStore.flash(FlashesStore.ERROR, message || 'Error fetching property data');
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PropertyShowLoadingActions.SET_ERROR, error: fetchError });
        }
      },
      stateDispatchFunc,
    });
  }, [propertyId, stateDispatchFunc]);

  // Second effect: Fetch time-dependent data when dateSpan changes (using cached static data)
  // Also handles full re-fetch after a cancelled initial load.
  useEffect(() => {
    if (!propertyId || !dateSpan) {
      logger.error('No property ID or date span found for time-dependent data fetching');
      return;
    }

    // Skip this effect on initial load - the first effect handles it
    if (isInitialLoadRef.current) {
      logger.log('Skipping second effect on initial load - first effect is handling it');
      return;
    }

    // Skip fetch when reverting datespan after cancellation — data is already in state
    if (skipNextFetchRef.current) {
      logger.log('Skipping fetch - datespan reverted after cancellation, data already in state');
      skipNextFetchRef.current = false;
      return;
    }

    logger.log('Fetching time-dependent data for date range:', dateSpan.start.toISODate(), 'to', dateSpan.finish.toISODate());

    if (stateDispatchFunc) {
      stateDispatchFunc({ type: PropertyShowLoadingActions.START_REQUEST });
    }

    // Create a fresh cancellation token for this request. Old in-flight requests
    // retain their own (cancelled) token, so they cannot be "uncancelled".
    const cancelToken = { current: false };
    cancelTokenRef.current = cancelToken;

    // If static data is not cached (e.g. after a cancelled initial load),
    // perform a full load including meters, rules, and time-series data.
    if (!staticDataCache.current.hasStaticData) {
      logger.log('No cached static data - performing full data load');
      getPropertyDataInBatches({
        propertyId,
        dateSpan,
        timezone,
        environment,
        historianAggregation,
        tradeAggregation,
        initialData,
        isFetchingRef: isFetchingData,
        cancelledRef: cancelToken,
        onDataReceived: (mergedData) => {
          logger.log('Received merged property data with property:', !!mergedData?.property);
          if (mergedData?.property?.meters?.edges) {
            const meters = mergedData.property.meters.edges.map((edge) => edge.node);
            const rulesData = meters.map((meter) => ({
              id: meter.id,
              rules: meter.rules,
            }));
            staticDataCache.current = {
              meters,
              rulesData,
              hasStaticData: true,
            };
            logger.log('Cached static data:', meters.length, 'meters');
          }
          setCombinedData(mergedData);
          if (stateDispatchFunc) {
            stateDispatchFunc({ type: PropertyShowLoadingActions.MARK_FULLY_LOADED });
          }
        },
        onError: (fetchError, message) => {
          logger.error('Error fetching property data:', fetchError);
          FlashesStore.flash(FlashesStore.ERROR, message || 'Error fetching property data');
          if (stateDispatchFunc) {
            stateDispatchFunc({ type: PropertyShowLoadingActions.SET_ERROR, error: fetchError });
          }
        },
        stateDispatchFunc,
      });
      return;
    }

    getTimeSeriesData({
      meters: staticDataCache.current.meters,
      rulesData: staticDataCache.current.rulesData,
      dateSpan,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      initialData,
      isFetchingRef: isFetchingData,
      cancelledRef: cancelToken,
      onDataReceived: (mergedData) => {
        setCombinedData(mergedData);
        // Dispatch fully loaded action to reducer
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PropertyShowLoadingActions.MARK_FULLY_LOADED });
        }
      },
      onError: (fetchError, message) => {
        logger.error('Error fetching time-dependent data:', fetchError);
        FlashesStore.flash(FlashesStore.ERROR, message || 'Error fetching time-dependent data');
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PropertyShowLoadingActions.SET_ERROR, error: fetchError });
        }
      },
      stateDispatchFunc,
      subsequentLoad: true,
    });
  }, [
    propertyId,
    dateSpan?.start,
    dateSpan?.finish,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    stateDispatchFunc,
  ]);

  const cancelFunc = useCallback(() => {
    cancelTokenRef.current.current = true;
    isFetchingData.current = false;
    // Allow subsequent date changes to trigger fetches via the second effect
    isInitialLoadRef.current = false;
    logger.log('Data fetch cancelled by user');
  }, []);

  const skipNextFetchFunc = useCallback(() => {
    skipNextFetchRef.current = true;
  }, []);

  return {
    combinedData,
    cancelFunc,
    skipNextFetchFunc,
  };
};

export default usePropertyData;
